import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

interface DeleteLessonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonTitle: string;
  lessonId: string;
  onConfirmDelete: (lessonId: string) => Promise<void>;
  isDeleting?: boolean;
}

export function DeleteLessonModal({
  open,
  onOpenChange,
  lessonTitle,
  lessonId,
  onConfirmDelete,
  isDeleting = false
}: DeleteLessonModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [localDeleting, setLocalDeleting] = useState(false);

  const isConfirmationValid = confirmText.trim() === lessonTitle.trim();
  const isProcessing = isDeleting || localDeleting;

  const handleConfirm = async () => {
    if (!isConfirmationValid || isProcessing) return;

    setLocalDeleting(true);
    try {
      await onConfirmDelete(lessonId);
      // Reset and close
      setConfirmText('');
      onOpenChange(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setLocalDeleting(false);
    }
  };

  const handleCancel = () => {
    if (isProcessing) return;
    setConfirmText('');
    onOpenChange(false);
  };

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isConfirmationValid && !isProcessing) {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={isProcessing ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl text-left">Delete Lesson?</DialogTitle>
              <DialogDescription className="text-left mt-1">
                This action cannot be undone
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-900 leading-relaxed">
              <strong className="font-semibold">Warning:</strong> Deleting this lesson will permanently remove:
            </p>
            <ul className="mt-2 ml-4 text-sm text-red-800 space-y-1 list-disc">
              <li>All slides and content</li>
              <li>All session history</li>
              <li>Student submissions and responses</li>
              <li>Grades and analytics data</li>
              <li>Shared access for other teachers</li>
            </ul>
          </div>

          {/* Lesson Title Display */}
          <div className="bg-muted/50 rounded-lg p-3 border border-border">
            <p className="text-sm text-muted-foreground mb-1">You are about to delete:</p>
            <p className="font-semibold text-foreground">{lessonTitle}</p>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <Label htmlFor="confirm-delete" className="text-sm font-medium">
              Type the lesson name to confirm deletion
            </Label>
            <Input
              id="confirm-delete"
              type="text"
              placeholder={`Type "${lessonTitle}" to confirm`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isProcessing}
              className={`${
                confirmText && !isConfirmationValid
                  ? 'border-red-500 focus-visible:ring-red-500'
                  : ''
              }`}
              autoComplete="off"
            />
            {confirmText && !isConfirmationValid && (
              <p className="text-xs text-red-600">
                The lesson name doesn't match. Please type it exactly as shown.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmationValid || isProcessing}
            className="w-full sm:w-auto"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Lesson
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
